import React from 'react';
import { LinearGradient } from 'expo-linear-gradient';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';
import { IconLink, TitleText, useResponsive } from '@garnish/core';

import { type DeliveryPreferenceType } from '@sg/graphql-schema';

import { Address } from '@order/features-locations';

import { useCustomer } from '../../../../context';
import { Modal } from '../../../Modal';

export const CompleteAddressFormModal = (props: CompleteAddressFormModalProps) => {
  const { address, isVisible, isAddingAddress, onRequestClose, onSubmit } = props;

  // ─────────────────────────────────────────────────────────────────────────────

  const { customer } = useCustomer();
  const customerAddresses = customer?.addresses ?? [];

  const { match } = useResponsive();
  const { formatMessage } = useIntl();
  const { top: safeAreaTopInset } = useSafeAreaInsets();
  const headerSafeAreaStyle = useStyle(
    () => ({
      paddingTop: safeAreaTopInset + theme.spacing['6'],
    }),
    [safeAreaTopInset],
  );

  return (
    <Modal
      fitHeight
      size={match(['full', 'small'])}
      accessibilityLabel={formatMessage(messages.a11yId)}
      visible={isVisible}
      onRequestClose={onRequestClose}
    >
      <View style={styles.headerContainer}>
        <View style={[styles.header, headerSafeAreaStyle]}>
          <IconLink
            name="IconArrowLeft"
            palette="muted"
            iconSize={24}
            width={36}
            height={36}
            accessibilityLabel={formatMessage(messages.returnToLocations)}
            onPress={onRequestClose}
          />

          <TitleText sizeMatch={['24']}>
            <FormattedMessage {...messages.title} />
          </TitleText>
        </View>

        <LinearGradient
          pointerEvents="none"
          colors={HEADER_GRADIENT_COLORS}
          locations={GRADIENT_LOCATIONS}
          style={styles.headerGradient}
        />
      </View>

      <Address.Form
        visible
        savedCustomerAddress={address}
        disableAddress
        customerAddresses={customerAddresses}
        onSubmit={onSubmit}
        isLoading={isAddingAddress}
        submitCtaOverride={formatMessage(messages.submitCta)}
      />
    </Modal>
  );
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  returnToLocations: {
    defaultMessage: 'Return to locations',
    description: 'Complete address form | Return to locations',
  },
  a11yId: {
    defaultMessage: 'Complete address form',
    description: 'Complete address form | a11y id',
  },
  title: {
    defaultMessage: 'Complete your delivery details',
    description: 'Complete address form | Title',
  },
  submitCta: {
    defaultMessage: 'Save & continue',
    description: 'Complete address form | Submit CTA',
  },
});

// ─── Constants ──────────────────────────────────────────────────────────────

const GRADIENT_HEIGHT = 24;
const GRADIENT_LOCATIONS = [0, 1] as const;
const HEADER_GRADIENT_COLORS = [
  theme.colors.APP_BACKGROUND,
  theme.colors.OPACITY.OATMEAL.TRANSPARENT,
] as const;

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  headerContainer: {
    zIndex: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
    marginBottom: theme.spacing['5'],
  },
  header: {
    paddingHorizontal: theme.spacing['4'],
    gap: theme.spacing['2'],
  },
  headerGradient: {
    position: 'absolute',
    bottom: -(GRADIENT_HEIGHT + theme.spacing['3']),
    left: 0,
    right: 0,
    height: GRADIENT_HEIGHT,
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type CompleteAddressFormModalProps = {
  address: CompleteAddressForm;
  isVisible: boolean;
  isAddingAddress: boolean;
  onRequestClose: () => void;
  onSubmit: (address: CompleteAddressForm) => void;
};

export type CompleteAddressForm = {
  id?: string;

  name: string;
  notes: string;
  deliveryPreference: DeliveryPreferenceType;

  street: string;
  secondaryStreet: string;
  city: string;
  country: string;
  state: string;
  zipCode: string;
};
